import { FeeEarnerLevel } from "@design-system";
import { Button, Collapse, Tabs } from "antd";
import React, { useState } from "react";
import { UnitHierarchies } from "./unit-hierarchies";
import styles from "./hierarchy-container.module.css";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

type PracticeArea = {
  id: number;
  description: string;
  order: number;
  parent?: PracticeArea | null;
  children?: PracticeArea[] | null;
};

type Props = {
  feeEarnerLevels: FeeEarnerLevel[] | undefined;
  practiceAreas: PracticeArea[] | undefined;
  updateFeeEarnerLevels: (data: { id: number; order: number }[]) => void;
  updatePracticeAreas: (data: { id: number; order: number }[]) => void;
  onSubmit: (activeTabKey: TabKey) => void;
};

type TabKey = "1" | "2";

const groupByParents = (practiceAreas: PracticeArea[]): PracticeArea[] => {
  const withParents = practiceAreas.filter((x) => x.parent?.id != null);

  const parents = practiceAreas.filter((x) => withParents.map((x) => x.parent?.id).includes(x.id));

  const grouped = parents.map((pa) => ({
    ...pa,
    children: practiceAreas.filter((x) => x.parent?.id === pa.id),
  }));

  return grouped;
};

export const HierarchyPage: React.FC<Props> = ({
  feeEarnerLevels,
  practiceAreas,
  updateFeeEarnerLevels,
  updatePracticeAreas,
  onSubmit,
}) => {
  const taxonomy = useTaxonomyLabel();

  const FE_LABEL = taxonomy.FeeEarner;
  const PA_LABEL = taxonomy.PracticeArea;
  const FE_LEVEL_LABEL = taxonomy.FeeEarnerLevel;

  const [activeTabKey, setActiveTabKey] = useState<TabKey>("1");

  const hasParents = practiceAreas?.some((x) => !!x.parent) || false;
  const groupedPracticeAreas = groupByParents(practiceAreas ?? []);

  const onFinished = (items: FeeEarnerLevel[] | PracticeArea[]) => {
    const updatePayload = items.map((x) => ({ id: x.id, order: x.order }));
    if (activeTabKey === "1") {
      updateFeeEarnerLevels(updatePayload);
    }
    if (activeTabKey === "2") {
      updatePracticeAreas(updatePayload);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <Tabs
          defaultActiveKey={activeTabKey}
          onTabClick={(key) => setActiveTabKey(key as TabKey)}
          type="card"
          className={styles.tabs}
          items={[
            {
              key: "1",
              label: `${FE_LEVEL_LABEL}s`,
              children: feeEarnerLevels && (
                <UnitHierarchies
                  items={feeEarnerLevels || []}
                  onFinished={(items: FeeEarnerLevel[]) => onFinished(items)}
                />
              ),
            },
            {
              key: "2",
              label: `${PA_LABEL}s`,
              children:
                practiceAreas && hasParents ? (
                  groupedPracticeAreas.map((pa) => {
                    return (
                      <Collapse key={pa.id}>
                        <Collapse.Panel key={pa.id} header={pa.description}>
                          <UnitHierarchies
                            items={pa.children || []}
                            onFinished={(items: PracticeArea[]) => onFinished(items)}
                          />
                        </Collapse.Panel>
                      </Collapse>
                    );
                  })
                ) : (
                  <UnitHierarchies
                    items={practiceAreas || []}
                    onFinished={(items: PracticeArea[]) => onFinished(items)}
                  />
                ),
            },
          ]}
        ></Tabs>
      </div>
      <div className={styles["submit-button"]}>
        <Button onClick={() => onSubmit(activeTabKey)} type="primary">
          Update {activeTabKey === "1" ? `${FE_LABEL} Levels` : `${PA_LABEL}s`}
        </Button>
      </div>
    </>
  );
};
